"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { PuffLoader } from "react-spinners";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "react-query";
import { getAllPropertiesPaginated, getAllPropertiesSummary } from "@/utils/api";
import { sortPropertiesByStatusPriority } from "@/utils/propertySorting";
import SearchWithTracking from "@/components/Search/SearchWithTracking";
import MultiPropertyMap from "@/components/MultiPropertyMap/MultiPropertyMap";
import { PropertyFilterBar } from "@/components/PropertyFilters/PropertyFilterBar";
import { Button } from "@/components/ui/button";
import GridPropertyCard from "@/components/PropertyCard/GridPropertyCard";
import {
  applyPropertyFilters,
  getDefaultFilters,
  resetAllFilters,
} from "@/utils/propertyFilterUtils";
import useAuthAwarePublicQueryGate from "@/components/hooks/useAuthAwarePublicQueryGate";
import { MapIcon, Grid3x3, ArrowUpDown, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const sortPropertiesByTractPriority = (properties, options = {}) => {
  if (!Array.isArray(properties)) return properties;
  const normalSortMode = options.normalSortMode || "preserve";

  const getCreatedAtMs = (property) => {
    const timestamp = new Date(property?.createdAt).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const getAskingPrice = (property) => {
    const parsed = Number(property?.askingPrice ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return properties
    .map((property, index) => ({
      property,
      index,
      hasTracts: Boolean(property?.hasTracts),
    }))
    .sort((left, right) => {
      // Rule 1: properties with tracts come first
      if (left.hasTracts !== right.hasTracts) {
        return left.hasTracts ? -1 : 1;
      }

      // Rule 2: when both properties have tracts, newest createdAt comes first
      if (left.hasTracts && right.hasTracts) {
        const dateDiff = getCreatedAtMs(right.property) - getCreatedAtMs(left.property);
        if (dateDiff !== 0) {
          return dateDiff;
        }
      }

      // For normal properties, support explicit price-desc ordering when requested.
      if (
        !left.hasTracts &&
        !right.hasTracts &&
        normalSortMode === "price-desc"
      ) {
        const priceDiff = getAskingPrice(right.property) - getAskingPrice(left.property);
        if (priceDiff !== 0) {
          return priceDiff;
        }
      }

      // Keep previous relative order for everything else
      return left.index - right.index;
    })
    .map(({ property }) => property);
};

export default function Properties() {
  const { authResolved } = useAuthAwarePublicQueryGate();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(getDefaultFilters());
  const [searchQuery, setSearchQuery] = useState("");

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    if (searchQuery) return true;
    if (filters.area !== "all") return true;
    if (filters.status !== "all") return true;
    if (filters.state !== "all") return true;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 10000000) return true;
    if (filters.acreRange[0] > 0 || filters.acreRange[1] < 1000) return true;
    if (filters.sqftRange[0] > 0 || filters.sqftRange[1] < 500000) return true;
    if (filters.city !== "all") return true;
    if (filters.county !== "all") return true;
    if (filters.zoning !== "all") return true;
    if (filters.financing !== "all") return true;
    if (filters.restrictions !== "all") return true;
    if (filters.water !== "all") return true;
    if (filters.sewer !== "all") return true;
    if (filters.electric !== "all") return true;
    if (filters.roadCondition !== "all") return true;
    if (filters.floodplain !== "all") return true;
    if (filters.hoaPoa !== "all") return true;
    if (filters.mobileHomeFriendly !== "all") return true;
    if (filters.featuredOnly) return true;
    return false;
  }, [filters, searchQuery]);

  // Use infinite query when no filters, regular query when filters active
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInfiniteLoading,
    isError: isInfiniteError,
  } = useInfiniteQuery(
    "propertiesInfinite",
    ({ pageParam = 1 }) => getAllPropertiesPaginated(pageParam, 20),
    {
      enabled: authResolved && !hasActiveFilters,
      getNextPageParam: (lastPage) => {
        return lastPage.pagination.hasNextPage
          ? lastPage.pagination.page + 1
          : undefined;
      },
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fallback to all data when filters are active
  const {
    data: allData,
    isLoading: isAllLoading,
    isError: isAllError,
  } = useInfiniteQuery(
    "propertiesAll",
    async () => {
      const data = await getAllPropertiesSummary();
      return { data, pagination: { page: 1, hasNextPage: false } };
    },
    {
      enabled: authResolved && hasActiveFilters,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Combine data from infinite query pages
  const data = useMemo(() => {
    if (hasActiveFilters && allData?.pages) {
      return allData.pages[0]?.data || [];
    }
    if (!hasActiveFilters && infiniteData?.pages) {
      return infiniteData.pages.flatMap((page) => page.data);
    }
    return [];
  }, [hasActiveFilters, infiniteData, allData]);

  const isLoading = hasActiveFilters ? isAllLoading : isInfiniteLoading;
  const isError = hasActiveFilters ? isAllError : isInfiniteError;
  const [viewMode, setViewMode] = useState("map");
  const [sortBy, setSortBy] = useState("price-desc");
  const [isMobile, setIsMobile] = useState(false);

  // Infinite scroll observer
  const loadMoreRef = useRef(null);

  useEffect(() => {
    if (!loadMoreRef.current || hasActiveFilters || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, hasActiveFilters]);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Force grid view on mobile
      if (window.innerWidth < 768) {
        setViewMode("grid");
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Initialize filters from URL search parameters
  useEffect(() => {
    const newFilters = getDefaultFilters();
    let hasUrlParams = false;

    // Check for area parameter
    const areaParam = searchParams.get("area");
    if (areaParam) {
      newFilters.area = areaParam;
      hasUrlParams = true;
    }

    // Check for featured parameter
    const featuredParam = searchParams.get("featured");
    if (featuredParam === "true") {
      newFilters.featuredOnly = true;
      hasUrlParams = true;
    }

    // Check for status parameter
    const statusParam = searchParams.get("status");
    if (statusParam) {
      newFilters.status = statusParam;
      hasUrlParams = true;
    }

    if (hasUrlParams) {
      setFilters(newFilters);
    }
  }, [searchParams]);

  // Apply filters and sorting
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = applyPropertyFilters(data, filters);

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((property) =>
        [
          property.streetAddress,
          property.city,
          property.state,
          property.zip,
          property.county,
          property.area,
          property.status,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [data, filters, searchQuery]);

  const sortedData = useMemo(() => {
    let sorted = [...filteredData];

    switch (sortBy) {
      case "price-desc":
        sorted = sortPropertiesByTractPriority(sorted, { normalSortMode: "price-desc" });
        break;
      case "price-asc":
        sorted = sorted.sort((a, b) => (Number(a.askingPrice) || 0) - (Number(b.askingPrice) || 0));
        break;
      case "size-desc":
        sorted = sorted.sort((a, b) => (Number(b.acre) || 0) - (Number(a.acre) || 0));
        break;
      case "size-asc":
        sorted = sorted.sort((a, b) => (Number(a.acre) || 0) - (Number(b.acre) || 0));
        break;
      case "newest":
        sorted = sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        sorted = sortPropertiesByStatusPriority(sorted);
    }

    return sorted;
  }, [filteredData, sortBy]);

  // Count active filters
  const countActiveFilters = () => {
    let count = 0;
    if (searchQuery) count++;
    if (filters.area !== "all") count++;
    if (filters.status !== "all") count++;
    if (filters.state !== "all") count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 10000000) count++;
    if (filters.acreRange[0] > 0 || filters.acreRange[1] < 1000) count++;
    if (filters.sqftRange[0] > 0 || filters.sqftRange[1] < 500000) count++;
    if (filters.city !== "all") count++;
    if (filters.county !== "all") count++;
    if (filters.zoning !== "all") count++;
    if (filters.financing !== "all") count++;
    if (filters.restrictions !== "all") count++;
    if (filters.water !== "all") count++;
    if (filters.sewer !== "all") count++;
    if (filters.electric !== "all") count++;
    if (filters.roadCondition !== "all") count++;
    if (filters.floodplain !== "all") count++;
    if (filters.hoaPoa !== "all") count++;
    if (filters.mobileHomeFriendly !== "all") count++;
    if (filters.featuredOnly) count++;
    return count;
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    resetAllFilters(setFilters, setSearchQuery);
  };

  // Handle property click
  const handlePropertyClick = (eventOrProperty, clickedProperty) => {
    const property = clickedProperty || eventOrProperty;

    if (clickedProperty) {
      eventOrProperty.preventDefault();
      eventOrProperty.stopPropagation();
    }

    window.open(`/properties/${property.id}`, "_blank", "noopener,noreferrer");
  };

  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FDF8F2]">
        <h2 className="text-red-600 text-xl font-semibold">Error fetching data.</h2>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FDF8F2]">
        <PuffLoader color="#4b5b4d" size={60} />
      </div>
    );
  }

  // Map View - Split Layout (50-50) - Desktop only with unified scroll
  if (viewMode === "map" && !isMobile) {
    return (
      <div className="bg-[#FDF8F2] min-h-screen">
        <div className="flex">
          {/* LEFT HALF - Sticky Map */}
          <div className="w-1/2 h-[calc(100vh-48px)] sticky top-0 p-3 flex items-start box-border">
            <div className="w-full h-full rounded-lg overflow-hidden border border-gray-200">
              <MultiPropertyMap properties={sortedData} onPropertyClick={handlePropertyClick} />
            </div>
          </div>

          {/* RIGHT HALF - Natural scrolling content */}
          <div className="w-1/2 min-h-screen">
            <div className="sticky top-0 z-20 bg-[#FDF8F2] shadow-sm">
              <div className="p-4 pb-2">
                {/* Compact Header */}
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <h1 className="text-xl font-bold text-[#324c48]">
                      Find Your Dream Property
                    </h1>

                    <div className="w-full sm:w-[340px] [&_input]:h-8 [&_button]:h-8 lg:w-[600px]">
                      <SearchWithTracking
                        query={searchQuery}
                        setQuery={setSearchQuery}
                        context="properties"
                        filteredData={sortedData}
                        filters={filters}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <PropertyFilterBar
                        filters={filters}
                        setFilters={setFilters}
                        onClearAll={handleClearAllFilters}
                        resultsCount={sortedData.length}
                        activeFilterCount={countActiveFilters()}
                        propertiesData={data}
                      />
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setViewMode("map")}
                          variant="default"
                          size="sm"
                          className="bg-[#324c48] hover:bg-[#3f4f24] h-8 text-xs"
                          id='properties-btn-map'>
                          <MapIcon className="w-3 h-3 mr-1" />
                          Map
                        </Button>
                        <Button
                          onClick={() => setViewMode("grid")}
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          id='properties-btn-grid'>
                          <Grid3x3 className="w-3 h-3 mr-1" />
                          Grid
                        </Button>
                      </div>

                      <Select value={sortBy} onValueChange={setSortBy} id='properties-select-1'>
                        <SelectTrigger className="w-[160px] h-8 text-xs" id='properties-select-trigger-1'>
                          <ArrowUpDown className="w-3 h-3 mr-1" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent id='properties-select-content-1'>
                          <SelectItem value="price-desc">Price: High to Low</SelectItem>
                          <SelectItem value="price-asc">Price: Low to High</SelectItem>
                          <SelectItem value="size-desc">Size: Large to Small</SelectItem>
                          <SelectItem value="size-asc">Size: Small to Large</SelectItem>
                          <SelectItem value="newest">Newest First</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              <hr className="border-t border-[#4b5b4d]/20" />
            </div>

            <div className="p-4 pt-3">
              {/* Properties Grid */}
              {sortedData.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <p className="text-gray-600 text-base mb-3">
                    No properties found matching your criteria.
                  </p>
                  <Button
                    onClick={handleClearAllFilters}
                    className="bg-[#324c48] hover:bg-[#3f4f24] text-white h-9 text-sm"
                    id='properties-btn-clear-all-filters'>
                    Clear All Filters
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 pb-4">
                    {sortedData.map((property) => (
                      <div
                        key={property.id}
                        className="transition hover:scale-[1.02] cursor-pointer"
                        onClickCapture={(event) => handlePropertyClick(event, property)}
                      >
                        <GridPropertyCard card={property} />
                      </div>
                    ))}
                  </div>

                  {/* Infinite scroll loading indicator */}
                  {!hasActiveFilters && (
                    <div ref={loadMoreRef} className="py-8 text-center">
                      {isFetchingNextPage ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-6 w-6 animate-spin text-[#324c48]" />
                          <span className="text-[#324c48] font-medium">Loading more properties...</span>
                        </div>
                      ) : hasNextPage ? (
                        <span className="text-gray-500 text-sm">Scroll for more</span>
                      ) : (
                        <span className="text-gray-500 text-sm">All properties loaded</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View - Full Width (also used on mobile)
  return (
    <div className="bg-[#FDF8F2] min-h-screen text-[#4b5b4d]">
      {/* Header - Compact */}
      <div className="sticky top-0 z-20 bg-[#FDF8F2] shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 py-2">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <h1 className="text-xl font-bold text-[#324c48]">
                Find Your Dream Property
              </h1>

              <div className="w-full sm:w-[240px] [&_input]:h-8 [&_button]:h-8 lg:w-[280px]">
                <SearchWithTracking
                  query={searchQuery}
                  setQuery={setSearchQuery}
                  context="properties"
                  filteredData={sortedData}
                  filters={filters}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1">
                <PropertyFilterBar
                  filters={filters}
                  setFilters={setFilters}
                  onClearAll={handleClearAllFilters}
                  resultsCount={sortedData.length}
                  activeFilterCount={countActiveFilters()}
                  propertiesData={data}
                />
              </div>

              {/* View Toggle & Sort - Hide map button on mobile */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex gap-2">
                  {!isMobile && (
                    <Button
                      onClick={() => setViewMode("map")}
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      id='properties-btn-map-2'>
                      <MapIcon className="w-3 h-3 mr-1" />
                      Map
                    </Button>
                  )}
                  <Button
                    onClick={() => setViewMode("grid")}
                    variant="default"
                    size="sm"
                    className="bg-[#324c48] hover:bg-[#3f4f24] h-8 text-xs"
                    id='properties-btn-grid-2'>
                    <Grid3x3 className="w-3 h-3 mr-1" />
                    Grid
                  </Button>
                </div>

                <Select value={sortBy} onValueChange={setSortBy} id='properties-select-2'>
                  <SelectTrigger className="w-[160px] h-8 text-xs" id='properties-select-trigger-2'>
                    <ArrowUpDown className="w-3 h-3 mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent id='properties-select-content-2'>
                    <SelectItem value="price-desc">Price: High to Low</SelectItem>
                    <SelectItem value="price-asc">Price: Low to High</SelectItem>
                    <SelectItem value="size-desc">Size: Large to Small</SelectItem>
                    <SelectItem value="size-asc">Size: Small to Large</SelectItem>
                    <SelectItem value="newest">Newest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 py-4">
        {sortedData.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-600 text-base mb-3">
              No properties found matching your criteria.
            </p>
            <Button
              onClick={handleClearAllFilters}
              className="bg-[#324c48] hover:bg-[#3f4f24] text-white h-9 text-sm"
              id='properties-btn-clear-all-filters-2'>
              Clear All Filters
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {sortedData.map((property) => (
                <div
                  key={property.id}
                  className="transition hover:scale-[1.02] cursor-pointer"
                  onClickCapture={(event) => handlePropertyClick(event, property)}
                >
                  <GridPropertyCard card={property} />
                </div>
              ))}
            </div>

            {/* Infinite scroll loading indicator */}
            {!hasActiveFilters && (
              <div ref={loadMoreRef} className="py-8 sm:py-12 text-center">
                {isFetchingNextPage ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-6 w-6 animate-spin text-[#324c48]" />
                    <span className="text-[#324c48] font-medium">Loading more properties...</span>
                  </div>
                ) : hasNextPage ? (
                  <span className="text-gray-500 text-sm">Scroll for more</span>
                ) : (
                  <span className="text-gray-500 text-sm">✓ All properties loaded</span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
